import { useRef, useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import Webcam from 'react-webcam';
import { useTranslation } from 'react-i18next';
import { SttButton } from '../../sporttia/buttons/SttButton';

interface WebcamCaptureProps {
    width?: number | string;
    height?: number | string;
    format?: 'image/jpeg' | 'image/png' | 'image/webp';
    onCapture?: (imageSrc: string | null) => void;
}

export default function WebcamCapture({
    width,
    height,
    format = 'image/jpeg',
    onCapture,
}: WebcamCaptureProps) {
    const { t } = useTranslation();

    const webcamRef = useRef<Webcam>(null);
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState<string | DOMException | boolean>(false);
    const [cameraReady, setCameraReady] = useState(false);

    useEffect(() => {
        navigator.mediaDevices.enumerateDevices().then((mediaDevices) => {
            setReady(true);
            setDevices(
                mediaDevices.filter(({ kind }: any) => kind === 'videoinput'),
            );
        });
    }, []);

    function capture(): void {
        if (webcamRef.current) {
            const imageSrc = webcamRef.current.getScreenshot();
            if (onCapture) onCapture(imageSrc);
        }
    }

    const devicesDetected = devices && devices.length > 0;

    if (!ready) {
        return null;
    }

    return (
        <Box display="flex" flexDirection="column" alignItems="center">
            {!devicesDetected && (
                <Box p={3} textAlign="center">
                    <p>{t('page.user.webcam.noWebcam')}</p>
                </Box>
            )}
            {error && (
                <Box p={3} textAlign="center">
                    <p>{t('page.user.webcam.webcamError')}</p>
                </Box>
            )}
            {devicesDetected && !error && (
                <>
                    <Box
                        mb={3}
                        textAlign="center"
                        width={width || 'auto'}
                        height={height || 'auto'}
                    >
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                border: !cameraReady
                                    ? '1px solid #ccc'
                                    : 'none',
                            }}
                        >
                            <Webcam
                                width="100%"
                                height="100%"
                                audio={false}
                                ref={webcamRef}
                                screenshotFormat={format}
                                onUserMedia={() => {
                                    setCameraReady(true);
                                }}
                                onUserMediaError={(
                                    err: string | DOMException,
                                ) => {
                                    setError(err);
                                }}
                            />
                            {!cameraReady && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }}
                                >
                                    <CircularProgress color="primary" />
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Box textAlign="center">
                        <SttButton
                            caption={t('page.user.webcam.capture')}
                            variant="contained"
                            color="primary"
                            onClick={capture}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
}
